import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Input, Tag, Modal, Typography, Space,
  Alert, Checkbox, App as AntApp, Flex, Empty,
} from 'antd';
import {
  CheckOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { fmtDataBr, somarDiasIso } from '../../utils/dataFmt';
import type { Transacao, CandidatoConciliacao, ResultadoBuscaManual } from './types';
import { BANCO_LABEL } from './types';
import { getToken, fmt } from './utils';

const { Text } = Typography;

// Item selecionado pro multi-vínculo — vale tanto pra candidato ranqueado
// quanto pra resultado da busca manual (os dois viram isto).
type ItemSel = {
  id: string; tipo: string; valor: number;
  valor_liquido?: number | null; label: string;
};

// Modal "Conciliar transação" — lista candidatos (CP/CR/transferência) com
// score, suporta multi-vínculo (CP: R$70 = R$48 + R$22 | CR: R$103k = 2 medições)
// e busca manual quando o auto-match não encontra.
export default function ModalConciliar({ transacao, onFechar, onConciliou, onCriarLancamento }: {
  transacao: Transacao; onFechar: () => void; onConciliou: () => void;
  onCriarLancamento: (t: Transacao) => void;
}) {
  const { message } = AntApp.useApp();
  const [candidatos, setCandidatos] = useState<CandidatoConciliacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [aplicando, setAplicando] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  // Busca manual: usado quando o auto-match não retorna o alvo desejado
  // (ex: parcela R$ 10.300 não aparece pra PIX R$ 4.150 entre 75 candidatos).
  const [buscaQ, setBuscaQ] = useState('');
  const [buscaResultados, setBuscaResultados] = useState<ResultadoBuscaManual[]>([]);
  const [buscaLoading, setBuscaLoading] = useState(false);
  const [mostrarBusca, setMostrarBusca] = useState(false);
  // Multi-vínculo: 1 OFX → N alvos (CP: R$70 = R$48 + R$22 | CR: R$103k = 2x R$51,6k)
  // Guarda o ITEM inteiro, não só o id: a seleção pode vir da lista ranqueada
  // OU da busca manual, e o somatório precisa do valor em qualquer caso.
  // (Antes era Set<string> e a soma vinha de `candidatos.filter(...)` — por
  // isso item achado na busca não entrava na conta e não dava pra juntar dois.)
  const [selecionadosCP, setSelecionadosCP] = useState<Map<string, ItemSel>>(new Map());
  const [selecionadosCR, setSelecionadosCR] = useState<Map<string, ItemSel>>(new Map());
  const [aplicandoMulti, setAplicandoMulti] = useState(false);
  // Mostra só os 5 primeiros candidatos + "ver mais" (pedido Lucas 2026-07-23):
  // o match quase sempre está nos 5 primeiros; o resto fica atrás do botão.
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const LIMITE_CANDIDATOS = 5;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true); setErro('');
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/financeiro/transacoes-bancarias/${transacao.id}/candidatos-conciliacao`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setCandidatos(d.candidatos || []);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  // Debounce 400ms — busca por digitação
  useEffect(() => {
    if (!mostrarBusca) return;
    if (buscaQ.trim().length < 2) { setBuscaResultados([]); return; }
    const tipo = transacao.valor > 0 ? 'cr' : 'cp';
    const handle = setTimeout(async () => {
      setBuscaLoading(true);
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/financeiro/parcelas-cr/buscar`, {
          params: { q: buscaQ.trim(), tipo },
          headers: { Authorization: token },
        });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setBuscaResultados(d.resultados || []);
      } catch (e: any) {
        message.error('Erro na busca: ' + (e.response?.data?.error || e.message));
      } finally { setBuscaLoading(false); }
    }, 400);
    return () => clearTimeout(handle);
    /* eslint-disable-next-line */
  }, [buscaQ, mostrarBusca]);

  async function aplicar(c: { tipo: string; id: string }) {
    setAplicando(c.id);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/transacoes-bancarias/${transacao.id}/conciliar`,
        { tipo: c.tipo, alvo_id: c.id }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success('Conciliada! ' + (d.efeitos || []).join(' · '));
      onConciliou();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicando(null); }
  }

  // Multi-vínculo: vincula N alvos a 1 transação OFX (CP ou CR)
  async function aplicarMulti(tipoMulti: 'lancamento_pagar' | 'parcela') {
    const ids = Array.from((tipoMulti === 'parcela' ? selecionadosCR : selecionadosCP).keys());
    if (ids.length === 0) return;
    setAplicandoMulti(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/financeiro/transacoes-bancarias/${transacao.id}/conciliar`,
        { tipo: tipoMulti, alvos_ids: ids }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      message.success((d.efeitos || []).join(' · '));
      onConciliou();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setAplicandoMulti(false); }
  }

  // Aceita o item inteiro (de qualquer origem) — é o que permite somar itens
  // vindos da busca manual junto com os da lista ranqueada.
  function toggleSelecionado(item: ItemSel) {
    const setter = item.tipo === 'parcela' ? setSelecionadosCR : setSelecionadosCP;
    setter(prev => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id); else next.set(item.id, item);
      return next;
    });
  }

  const ehReceita = transacao.valor > 0;
  const valorAbsTransacao = Math.abs(transacao.valor);

  // Janela do auto-match — espelha o filtro do backend
  // (_buscarCandidatosConciliacao: ±60 dias em CP/CR). É FILTRO, não ranking:
  // o que vence fora disso não entra nem na contagem de candidatos, e a
  // única saída é a busca manual (que não filtra por data). Sem essa linha na
  // tela, "55 candidatos" parecia lista completa — caso Riosul, 24/08/2026.
  const JANELA_DIAS = 60;
  const janelaIni = somarDiasIso(transacao.data_transacao, -JANELA_DIAS);
  const janelaFim = somarDiasIso(transacao.data_transacao, JANELA_DIAS);
  const janelaTexto = janelaIni && janelaFim
    ? `de ${fmtDataBr(janelaIni)} a ${fmtDataBr(janelaFim)}`
    : `até ${JANELA_DIAS} dias desta data`;
  const alvoLabel = ehReceita ? 'parcela' : 'lançamento';
  const alvoDataLabel = ehReceita ? 'vencimento' : 'vencimento ou pagamento';

  // Pool CP selecionado — soma vem da própria seleção (não de `candidatos`),
  // pra incluir o que foi achado na busca manual.
  const candidatosCPSel = Array.from(selecionadosCP.values());
  const somaSelecionadosCP = candidatosCPSel.reduce((s, c) => s + Math.abs(c.valor), 0);
  const diffSelCP = valorAbsTransacao - somaSelecionadosCP;
  const matchExatoCP = Math.abs(diffSelCP) <= 0.01;
  const matchAproxCP = Math.abs(diffSelCP) <= 0.50;

  // Pool CR selecionado: tenta bater bruto OU líquido (ECOPLAN paga líquido)
  const candidatosCRSel = Array.from(selecionadosCR.values());
  const somaCRBruto = candidatosCRSel.reduce((s, c) => s + Math.abs(c.valor), 0);
  const somaCRLiquido = candidatosCRSel.reduce((s, c) => s + Math.abs(c.valor_liquido || c.valor), 0);
  const diffCRBruto = Math.abs(valorAbsTransacao - somaCRBruto);
  const diffCRLiquido = Math.abs(valorAbsTransacao - somaCRLiquido);
  const matchExatoCR = diffCRBruto <= 0.01 || diffCRLiquido <= 0.01;
  const matchAproxCR = diffCRBruto <= 0.50 || diffCRLiquido <= 0.50;
  const usarLiquidoCR = !matchExatoCR && diffCRLiquido < diffCRBruto;
  const somaCRRef = usarLiquidoCR ? somaCRLiquido : somaCRBruto;
  const diffCRRef = usarLiquidoCR ? diffCRLiquido : diffCRBruto;

  // Compat: variaveis usadas no JSX existente (CP) — apontam pro CP
  const somaSelecionados = somaSelecionadosCP;
  const diffSelecionados = diffSelCP;
  const matchExato = matchExatoCP;
  const matchAprox = matchAproxCP;

  // Saldo que ainda falta casar quando ja ha selecao (aglutinacao 1 OFX = N).
  // Guia o usuario a achar a parcela/CP que completa a soma: os candidatos cujo
  // valor bate esse restante ficam VERDES e sobem pro topo.
  const temSelCP = selecionadosCP.size > 0;
  const temSelCR = selecionadosCR.size > 0;
  const faltaCasar = temSelCP ? diffSelCP
    : temSelCR ? (valorAbsTransacao - somaCRRef)
    : null;  // null = ainda sem selecao
  function fechaSoma(c: CandidatoConciliacao): boolean {
    if (faltaCasar == null || faltaCasar <= 0.01) return false;
    const alvo = (temSelCP && c.tipo === 'lancamento_pagar')
      || (temSelCR && c.tipo === 'parcela');
    if (!alvo) return false;
    const v = Math.abs(c.valor);
    const vl = c.valor_liquido ? Math.abs(c.valor_liquido) : v;
    return Math.abs(v - faltaCasar) <= 0.50 || Math.abs(vl - faltaCasar) <= 0.50;
  }
  // Ordena: selecionados no topo, depois os que fecham a soma, depois por score.
  const candidatosOrdenados = [...candidatos].sort((a, b) => {
    const aSel = (selecionadosCP.has(a.id) || selecionadosCR.has(a.id)) ? 1 : 0;
    const bSel = (selecionadosCP.has(b.id) || selecionadosCR.has(b.id)) ? 1 : 0;
    if (aSel !== bSel) return bSel - aSel;
    const aF = fechaSoma(a) ? 1 : 0;
    const bF = fechaSoma(b) ? 1 : 0;
    if (aF !== bF) return bF - aF;
    return (b.score || 0) - (a.score || 0);
  });

  return (
    <Modal open onCancel={onFechar} footer={null} width={760} title="🔗 Conciliar transação">
      <Card size="small" style={{ background: '#FAFBFF', marginBottom: 16 }} bodyStyle={{ padding: 10 }}>
        <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>{fmtDataBr(transacao.data_transacao)}</Text>
        <div><Text style={{ fontSize: 13 }}>{transacao.descricao}</Text></div>
        <Flex justify="space-between" align="baseline">
          <Text type="secondary" style={{ fontSize: 10 }}>
            {BANCO_LABEL[transacao.banco] || transacao.banco} {transacao.conta_id} · {transacao.tipo_ofx}
          </Text>
          <Text style={{ fontSize: 18, fontWeight: 700, color: ehReceita ? '#0a7f4a' : '#B91626' }}>
            {ehReceita ? '+' : ''}{fmt(transacao.valor)}
          </Text>
        </Flex>
      </Card>

      {/* Ações no TOPO (pedido Lucas 2026-07-23): criar lançamento novo + busca
          manual — antes ficavam no fim do modal. */}
      {!loading && !erro && (
        <div style={{ marginBottom: 14 }}>
          <Flex gap={8} wrap="wrap">
            <Button onClick={() => onCriarLancamento(transacao)}
              style={{ color: '#020D7F', borderColor: '#020D7F', fontWeight: 600, flex: '1 1 240px' }}>
              📝 Criar lançamento novo (definir categoria DRE)
            </Button>
            <Button type="dashed"
              onClick={() => { setMostrarBusca(v => !v); setBuscaQ(''); setBuscaResultados([]); }}
              style={{ flex: '1 1 200px', fontWeight: 500 }}>
              🔍 {mostrarBusca ? 'Fechar busca' : `Buscar ${ehReceita ? 'parcela' : 'lançamento'} manualmente`}
            </Button>
          </Flex>
          {mostrarBusca && (
            <div style={{ marginTop: 10 }}>
              <Input
                placeholder={ehReceita
                  ? 'Cliente, CNPJ, contrato, título da parcela…'
                  // "loja" é o `emissor` do reembolso: quem procura o gasto
                  // procura pelo nome da oficina/posto, não pela pessoa que
                  // recebeu o Pix. Anunciar aqui é o que torna isso achável.
                  : 'Fornecedor, loja, descrição, categoria…'}
                value={buscaQ}
                onChange={e => setBuscaQ(e.target.value)}
                allowClear autoFocus
                style={{ marginBottom: 8 }}
              />
              {buscaLoading && <Text type="secondary" style={{ fontSize: 11 }}>buscando…</Text>}
              {!buscaLoading && buscaQ.trim().length >= 2 && buscaResultados.length === 0 && (
                <Text type="secondary" style={{ fontSize: 11 }}>nenhum resultado</Text>
              )}
              {buscaResultados.length > 0 && (
                <>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                    Marque <strong>duas ou mais</strong> pra vincular juntas — é o caso de um PIX
                    que paga vários lançamentos (ex.: dois reembolsos na mesma transferência).
                  </Text>
                  <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
                    {buscaResultados.map(r => {
                      const valoresDif = Math.abs(r.valor - Math.abs(transacao.valor)) > 0.01;
                      const liqMatch = r.valor_liquido && Math.abs(r.valor_liquido - Math.abs(transacao.valor)) <= 0.01;
                      // O que a pessoa quer saber antes de clicar: isto ZERA o
                      // saldo? (Lucas, 2026-08-24.) `falta` vem do backend pela
                      // mesma conta da aba Pagamento da parcela: alvo líquido
                      // menos o que já entrou. Sem ele, o cartão mostrava
                      // "R$ 44.000 · já recebido R$ 36.294" e cabia ao usuário
                      // fazer a subtração de cabeça — e a tag dizia
                      // "~ diferente" comparando com o BRUTO, o que faz um
                      // pagamento final exato parecer um valor que não bate.
                      const falta = r.falta != null ? r.falta : null;
                      const valorTx = Math.abs(transacao.valor);
                      const quita = falta != null && falta > 0.01 && valorTx >= falta - 0.02;
                      const sobra = falta != null ? Math.max(0, falta - valorTx) : null;
                      const casaFalta = falta != null && Math.abs(falta - valorTx) <= 0.02;
                      const tagCor = !valoresDif || liqMatch || casaFalta ? 'success' : 'default';
                      const sel = (r.tipo === 'parcela' ? selecionadosCR : selecionadosCP).has(r.id);
                      // Este resultado fecha exatamente o que falta da soma?
                      const fecha = faltaCasar != null && faltaCasar > 0.01
                        && Math.abs(Math.abs(r.valor) - faltaCasar) <= 0.50;
                      return (
                        <div key={r.tipo + '_' + r.id}
                          style={{ padding: 8, borderBottom: '0.5px solid #f0f0f0',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                            background: sel ? '#E8EEFF' : fecha ? '#E8F5EE' : undefined }}>
                          <Checkbox checked={sel}
                            onChange={() => toggleSelecionado({
                              id: r.id, tipo: r.tipo, valor: r.valor,
                              valor_liquido: r.valor_liquido, label: r.label,
                            })}
                            title="Marque 2+ pra vincular juntas a esta transação" />
                          <div style={{ flex: 1 }}>
                            <Space size={4} style={{ marginBottom: 2 }} wrap>
                              <Tag color={tagCor}>
                                {!valoresDif ? '✓ bruto' : liqMatch ? '✓ líquido'
                                  : casaFalta ? '✓ o que falta' : '~ diferente'}
                              </Tag>
                              {quita
                                ? <Tag color="success">✓ QUITA (zera o saldo)</Tag>
                                : sobra != null && sobra > 0.01
                                  && <Tag color="warning">⚠ PARCIAL · ainda faltará {fmt(sobra)}</Tag>}
                              {fecha && <Tag color="success">→ FECHA A SOMA</Tag>}
                              {r.status && r.status !== 'pendente' && (<Tag>{r.status}</Tag>)}
                            </Space>
                            <div><Text strong style={{ fontSize: 12 }}>{r.label}</Text></div>
                            <div>
                              <Text type="secondary" style={{ fontSize: 10 }}>
                                {r.sub} · venc {fmtDataBr(r.data)} · {fmt(r.valor)}
                                {/* !! nos dois: `0 && <>…</>` devolve 0, e o React
                                    IMPRIME o zero — era um "0" solto no fim da linha
                                    de toda parcela sem recebimento. */}
                                {!!r.valor_liquido && r.valor_liquido !== r.valor && (<> · líq {fmt(r.valor_liquido)}</>)}
                                {!!r.valor_recebido && r.valor_recebido > 0 && (<> · já recebido {fmt(r.valor_recebido)}</>)}
                              </Text>
                            </div>
                            {/* Só quando ainda há o que receber/pagar — em parcela
                                quitada a linha viraria "falta R$ 0,00" e só polui. */}
                            {falta != null && falta > 0.01 && (
                              <div>
                                <Text style={{ fontSize: 11, fontWeight: 600,
                                  color: quita ? '#0a7f4a' : '#B07500' }}>
                                  falta {fmt(falta)} → depois deste vínculo: {fmt(sobra ?? 0)}
                                  {quita && ' (parcela quitada)'}
                                </Text>
                              </div>
                            )}
                          </div>
                          <Button size="small" type="primary" icon={<CheckOutlined />}
                            loading={aplicando === r.id}
                            style={quita ? { background: '#0a7f4a', borderColor: '#0a7f4a' } : undefined}
                            onClick={() => aplicar({ tipo: r.tipo, id: r.id })}>
                            {quita ? 'Vincular e quitar' : 'Vincular só esta'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Banners de multi-vínculo — FORA do bloco de candidatos (antes ficavam
          dentro, e sumiam quando não havia candidato ranqueado ou quando a
          seleção vinha só da busca manual). Listam os itens escolhidos porque
          eles podem não estar visíveis em nenhuma das duas listas. */}
      {selecionadosCP.size > 0 && (
        <div style={{
          padding: '10px 14px',
          background: matchExato ? '#E8F5EE' : matchAprox ? '#FFF8E8' : '#FDECEC',
          border: `1.5px solid ${matchExato ? '#0a7f4a' : matchAprox ? '#B07500' : '#B91626'}`,
          borderRadius: 6, marginBottom: 12,
        }}>
          <Flex justify="space-between" align="center" gap={10} wrap="wrap">
            <div style={{ flex: '1 1 320px' }}>
              <Text strong style={{ fontSize: 13, display: 'block' }}>
                {matchExato ? '✓' : matchAprox ? '⚠' : '✗'} {selecionadosCP.size} despesa{selecionadosCP.size > 1 ? 's' : ''} selecionada{selecionadosCP.size > 1 ? 's' : ''} ·
                soma R$ {somaSelecionados.toFixed(2).replace('.', ',')}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                OFX: R$ {valorAbsTransacao.toFixed(2).replace('.', ',')} ·
                {matchExato ? ' bate exato' :
                  diffSelecionados > 0
                    ? ` falta R$ ${diffSelecionados.toFixed(2).replace('.', ',')}`
                    : ` ultrapassa R$ ${Math.abs(diffSelecionados).toFixed(2).replace('.', ',')}`}
              </Text>
              <div style={{ marginTop: 4 }}>
                {candidatosCPSel.map(i => (
                  <Tag key={i.id} closable onClose={() => toggleSelecionado(i)}
                    style={{ fontSize: 10, marginBottom: 2 }}>
                    {i.label.slice(0, 34)} · {fmt(i.valor)}
                  </Tag>
                ))}
              </div>
            </div>
            <Space>
              <Button size="small" onClick={() => setSelecionadosCP(new Map())}>Limpar</Button>
              <Button
                type="primary" icon={<CheckOutlined />} loading={aplicandoMulti}
                disabled={selecionadosCP.size < 2}
                onClick={() => aplicarMulti('lancamento_pagar')}
                style={{
                  background: matchExato ? '#0a7f4a' : matchAprox ? '#B07500' : '#B91626',
                  borderColor: matchExato ? '#0a7f4a' : matchAprox ? '#B07500' : '#B91626',
                }}>
                Vincular {selecionadosCP.size} despesa{selecionadosCP.size > 1 ? 's' : ''}
              </Button>
            </Space>
          </Flex>
        </div>
      )}

      {selecionadosCR.size > 0 && (
        <div style={{
          padding: '10px 14px',
          background: matchExatoCR ? '#E8F5EE' : matchAproxCR ? '#FFF8E8' : '#FDECEC',
          border: `1.5px solid ${matchExatoCR ? '#0a7f4a' : matchAproxCR ? '#B07500' : '#B91626'}`,
          borderRadius: 6, marginBottom: 12,
        }}>
          <Flex justify="space-between" align="center" gap={10} wrap="wrap">
            <div style={{ flex: '1 1 320px' }}>
              <Text strong style={{ fontSize: 13, display: 'block' }}>
                {matchExatoCR ? '✓' : matchAproxCR ? '⚠' : '✗'} {selecionadosCR.size} parcela{selecionadosCR.size > 1 ? 's' : ''} selecionada{selecionadosCR.size > 1 ? 's' : ''} ·
                soma {usarLiquidoCR ? 'líquida' : 'bruta'} R$ {somaCRRef.toFixed(2).replace('.', ',')}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                OFX: R$ {valorAbsTransacao.toFixed(2).replace('.', ',')} ·
                {matchExatoCR ? ' bate exato' : ` diff R$ ${diffCRRef.toFixed(2).replace('.', ',')}`}
                {!matchExatoCR && diffCRBruto !== diffCRLiquido && (
                  <> · <Text type="secondary" style={{ fontSize: 10 }}>
                    (bruto R$ {somaCRBruto.toFixed(2).replace('.', ',')} · líq R$ {somaCRLiquido.toFixed(2).replace('.', ',')})
                  </Text></>
                )}
              </Text>
              <div style={{ marginTop: 4 }}>
                {candidatosCRSel.map(i => (
                  <Tag key={i.id} closable onClose={() => toggleSelecionado(i)}
                    style={{ fontSize: 10, marginBottom: 2 }}>
                    {i.label.slice(0, 34)} · {fmt(i.valor)}
                  </Tag>
                ))}
              </div>
            </div>
            <Space>
              <Button size="small" onClick={() => setSelecionadosCR(new Map())}>Limpar</Button>
              <Button
                type="primary" icon={<CheckOutlined />} loading={aplicandoMulti}
                disabled={selecionadosCR.size < 2}
                onClick={() => aplicarMulti('parcela')}
                style={{
                  background: matchExatoCR ? '#0a7f4a' : matchAproxCR ? '#B07500' : '#B91626',
                  borderColor: matchExatoCR ? '#0a7f4a' : matchAproxCR ? '#B07500' : '#B91626',
                }}>
                Vincular {selecionadosCR.size} parcela{selecionadosCR.size > 1 ? 's' : ''}
              </Button>
            </Space>
          </Flex>
        </div>
      )}

      {loading ? <Card loading />
        : erro ? <Alert type="error" message={erro} showIcon />
        : candidatos.length === 0 ? (
          <Empty description={
            <Text style={{ fontSize: 11 }}>
              Não há parcela CR / lançamento CP com {ehReceita ? 'mesmo valor a receber' : 'mesmo valor a pagar'}
              {' '}e {alvoDataLabel} {janelaTexto}.
              <br />Fora dessa janela só pela <strong>🔍 Buscar {alvoLabel} manualmente</strong> aí em cima — a busca não filtra por data.
            </Text>
          } />
        ) : (
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              {candidatos.length} candidato{candidatos.length > 1 ? 's' : ''} encontrado{candidatos.length > 1 ? 's' : ''}, ordenado por compatibilidade
              {!mostrarTodos && candidatos.length > LIMITE_CANDIDATOS && ` — mostrando os ${LIMITE_CANDIDATOS} mais compatíveis`}:
            </Text>
            {/* A contagem acima é de quem PASSOU no filtro de data, não de tudo
                que existe — sem dizer isso, "55 candidatos" lia-se como lista
                completa e a parcela velha parecia não existir (Riosul, 24/08/2026). */}
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 10 }}>
              Só entram {ehReceita ? 'parcelas' : 'lançamentos'} com {alvoDataLabel} {janelaTexto}. O que está fora
              não aparece aqui <em>nem entra nessa conta</em> — pra esses, use a <strong>🔍 Buscar {alvoLabel} manualmente</strong> aí em cima.
            </Text>

            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {(mostrarTodos ? candidatosOrdenados : candidatosOrdenados.slice(0, LIMITE_CANDIDATOS)).map(c => {
                const ehHigh = c.score >= 180;
                const ehParcial = !!c._parcial;
                // Parcial que ZERA o saldo = este vínculo quita/completa → verde
                const ehParcialQuita = ehParcial && c._saldoApos != null && Math.abs(c._saldoApos) <= 0.02;
                // Casa o saldo restante de uma aglutinação em andamento → verde
                const completaSoma = fechaSoma(c);
                const ehAfinidade = !!c._afinidade;
                const ehMesmaPessoa = !!c._mesmaPessoa;
                const ehCP = c.tipo === 'lancamento_pagar';
                const ehCR = c.tipo === 'parcela';
                const selecionado = (ehCP && selecionadosCP.has(c.id))
                  || (ehCR && selecionadosCR.has(c.id));
                const tipoEmoji = c.tipo === 'parcela' ? '📥' : c.tipo === 'lancamento_pagar' ? '📤' : '🔄';
                const tipoLabel = c.tipo === 'parcela' ? 'Receita (CR)' : c.tipo === 'lancamento_pagar' ? 'Despesa (CP)' : c.tipo === 'transferencia_ca' ? 'Transferência (Conta Azul)' : 'Transferência';
                // Prioridade visual: selecionado > parcial > afinidade > high
                // (mesmaPessoa pode coexistir com high — usa cor azul claro pra
                // fundo sem competir com verde do high)
                const corBorda = selecionado ? '#020D7F' : completaSoma ? '#0a7f4a' : ehParcialQuita ? '#0a7f4a' : ehParcial ? '#B07500' : ehAfinidade ? '#0930D0' : ehHigh ? '#0a7f4a' : ehMesmaPessoa ? '#0930D0' : undefined;
                const corBg = selecionado ? '#E8EEFF' : completaSoma ? '#E8F5EE' : ehParcialQuita ? '#F4FBF6' : ehParcial ? '#FFF8E8' : ehAfinidade ? '#EFF3FF' : ehHigh ? '#F4FBF6' : ehMesmaPessoa ? '#EFF3FF' : undefined;
                return (
                  <Card key={c.tipo + '_' + c.id} size="small"
                    style={{ borderColor: corBorda, background: corBg, borderWidth: selecionado ? 1.5 : undefined }}>
                    <Flex justify="space-between" align="center" gap={12}>
                      {/* Checkbox p/ multi-vinculo (CP ou CR) */}
                      {(ehCP || ehCR) && (
                        <Checkbox
                          checked={selecionado}
                          onChange={() => toggleSelecionado({
                            id: c.id, tipo: c.tipo, valor: c.valor,
                            valor_liquido: c.valor_liquido, label: c.label,
                          })}
                          title="Selecione 2+ pra vincular juntos a esta transação"
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <Space style={{ marginBottom: 4 }}>
                          <Tag color={ehParcialQuita ? 'success' : ehParcial ? 'warning' : ehAfinidade ? 'blue' : ehHigh ? 'success' : 'default'}>
                            {tipoEmoji} {tipoLabel}
                          </Tag>
                          {completaSoma && <Tag color="success">→ FECHA A SOMA</Tag>}
                          {ehParcialQuita
                            ? <Tag color="success">✓ QUITA (zera o saldo)</Tag>
                            : ehParcial && <Tag color="warning">⚠ PARCIAL</Tag>}
                          {/* MESMA PESSOA: tag aparece sempre que nome bate, mesmo
                              com valor exato (antes só vinha por afinidade) */}
                          {ehMesmaPessoa && !ehAfinidade && <Tag color="blue">🎯 MESMA PESSOA</Tag>}
                          {ehAfinidade && <Tag color="blue">🎯 MESMA PESSOA · valor difere</Tag>}
                          <Text type="secondary" style={{ fontSize: 10,
                            color: corBorda || '#666', fontWeight: 600 }}>
                            score {c.score} {ehHigh && !ehParcial && !ehAfinidade && '(HIGH)'}
                          </Text>
                        </Space>
                        <div><Text strong style={{ fontSize: 13 }}>{c.label}</Text></div>
                        <div><Text type="secondary" style={{ fontSize: 11 }}>{c.sub} · venc/data {fmtDataBr(c.data)} · valor {fmt(c.valor)}</Text></div>
                      </div>
                      <Button
                        type="primary" icon={<CheckOutlined />} loading={aplicando === c.id}
                        style={corBorda && !selecionado ? { background: corBorda, borderColor: corBorda } : undefined}
                        onClick={() => aplicar(c)}
                      >
                        {ehParcialQuita ? 'Vincular e quitar' : ehParcial ? 'Vincular parcial' : ehAfinidade ? 'Vincular (valor difere)' : 'Vincular'}
                      </Button>
                    </Flex>
                  </Card>
                );
              })}
            </Space>
            {!mostrarTodos && candidatosOrdenados.length > LIMITE_CANDIDATOS && (
              <Button block type="link" onClick={() => setMostrarTodos(true)} style={{ marginTop: 8 }}>
                Ver mais {candidatosOrdenados.length - LIMITE_CANDIDATOS} candidato{candidatosOrdenados.length - LIMITE_CANDIDATOS > 1 ? 's' : ''} ▾
              </Button>
            )}
          </div>
        )}
    </Modal>
  );
}
